/** Formattazioni per la UI, tutte in italiano. */

export function formatSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Sotto i 10 mostra un decimale: "1,4 MB" dice più di "1 MB".
  const decimals = value < 10 ? 1 : 0;
  return `${value.toFixed(decimals).replace('.', ',')} ${units[unit]}`;
}

const dateFormat = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : dateFormat.format(date);
}

/** Etichetta per tipo di file, dall'estensione. */
export function fileKindLabel(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const labels: Record<string, string> = {
    pdf: 'PDF',
    p7m: 'P7M',
    xml: 'XML',
    xlsx: 'Excel',
    xls: 'Excel',
    csv: 'CSV',
    docx: 'Word',
    doc: 'Word',
    jpg: 'Immagine',
    jpeg: 'Immagine',
    png: 'Immagine',
    zip: 'Archivio',
    txt: 'Testo',
  };
  return labels[ext] ?? (ext ? ext.toUpperCase() : 'File');
}

/* --- orari dell'audit ------------------------------------------------------
 *
 * Il fuso e' fissato a `Europe/Rome`, non lasciato a quello del dispositivo.
 *
 * `formatDate` qui sopra non passa `timeZone`, quindi segue l'orologio di chi
 * guarda: per la data di modifica di un file va benissimo. Per un registro di
 * prove no: due amministratori collegati da fusi diversi leggerebbero istanti
 * diversi per la stessa riga, e un CSV allegato a una contestazione dipenderebbe
 * da chi lo ha esportato. Lo studio e i clienti stanno in Italia, quindi l'ora
 * italiana e' quella dei fatti: si scrive una volta e non cambia piu'.
 *
 * L'istante immagazzinato resta UTC (`timestamptz`): qui si cambia solo come lo
 * si legge. Il valore UTC crudo resta visibile nel dettaglio della riga, perche'
 * e' quello che combacia con i log applicativi del server.
 * ------------------------------------------------------------------------- */

export const FUSO_AUDIT = 'Europe/Rome';

const oraAudit = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: FUSO_AUDIT,
});

const istanteAudit = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: FUSO_AUDIT,
});

/** `28/08/2026, 16:04` in ora italiana. Per la colonna della tabella. */
export function formatOraAudit(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : oraAudit.format(d);
}

/**
 * `28/08/2026, 16:04:27,013` in ora italiana.
 *
 * I millisecondi si aggiungono a mano: `fractionalSecondDigits` esiste, ma
 * mette il separatore decimale della locale in mezzo all'ora ed esce
 * `16:04:27,013` in un modo che dipende dalla locale. Meglio comporlo.
 */
export function formatIstanteAudit(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${istanteAudit.format(d)},${ms}`;
}
